import { useEffect, useMemo, useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { useAuth } from '../auth/AuthContext';
import { useGoalsConfig } from '../utils/goalsLocalStore';
import { fetchActionCheckins } from '../api/goalsApi';
import {
  FOCUS_SLOTS,
  getFocusDef,
  readFocusActual,
  resolveFocusSelection,
  resolveSubKgi,
  UNSELECTED_FOCUS,
} from '../utils/kpiFocusCatalog';
import { CHECKIN_REASONS } from '../constants/checkinReasons';

const WEEKDAY = ['日', '月', '火', '水', '木', '金', '土'];

function parseMonth(selectedMonth) {
  if (!selectedMonth || !/^\d{4}-\d{2}$/.test(selectedMonth)) {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, label: `${now.getMonth() + 1}月` };
  }
  const [y, m] = selectedMonth.split('-').map(Number);
  return { year: y, month: m, label: `${m}月` };
}

function formatTodayJa(d = new Date()) {
  return `${d.getMonth() + 1}月${d.getDate()}日（${WEEKDAY[d.getDay()]}）`;
}

function formatShortDate(d = new Date()) {
  return `${d.getMonth() + 1}/${d.getDate()}（${WEEKDAY[d.getDay()]}）`;
}

function formatDateLabel(d) {
  return `${d.getMonth() + 1}月${d.getDate()}日（${WEEKDAY[d.getDay()]}）`;
}

function formatTime(d) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatGroup(d, todayStr) {
  const day = d.toISOString().slice(0, 10);
  if (day === todayStr) return '今日';
  const today = new Date(todayStr);
  const yesterday = new Date(today.getTime() - 86400000).toISOString().slice(0, 10);
  if (day === yesterday) return '昨日';
  return formatDateLabel(d);
}

/** Map backend session summary to AiHistory / AiSession UI shape. */
export function mapApiSessionSummary(s, todayStr) {
  const d = s.updated_at ? new Date(s.updated_at) : new Date();
  const who = [s.store, s.stylist].filter(Boolean).join(' / ');
  return {
    id: s.id || s.session_id,
    group: formatGroup(d, todayStr),
    date: d.toISOString().slice(0, 10),
    dateLabel: formatDateLabel(d),
    time: formatTime(d),
    title: s.title || 'AI相談',
    icon: s.icon || '💬',
    preview: who ? `${who} — ${s.preview || ''}` : (s.preview || ''),
    message_count: s.message_count ?? 0,
    store: s.store || null,
    stylist: s.stylist || null,
    month: s.month || null,
    messages: [],
  };
}

/** Map backend session detail messages to chat bubbles. */
export function mapApiSessionDetail(apiSession, todayStr) {
  const base = mapApiSessionSummary(
    {
      id: apiSession.session_id,
      title: apiSession.title,
      icon: apiSession.icon,
      preview: apiSession.preview,
      updated_at: apiSession.updated_at,
      message_count: (apiSession.messages || []).length,
      store: apiSession.store,
      stylist: apiSession.stylist,
      month: apiSession.month,
    },
    todayStr
  );
  base.messages = (apiSession.messages || []).map((m) => ({
    role: m.role === 'user' ? 'user' : 'ai',
    text: m.content,
    choices: m.choices || [],
  }));
  return base;
}

/**
 * AI tab adapter — merges dashboard KPIs; sessions supplied by AiStack from API.
 */
function mapGoalsActions(goals) {
  const actions = goals?.actions;
  if (!Array.isArray(actions) || actions.length === 0) return [];
  return actions.slice(0, 3).map((a, idx) => {
    const doneCount = a.doneCount != null ? Number(a.doneCount) : null;
    const rate =
      doneCount != null && Number.isFinite(doneCount)
        ? `今月 ${doneCount}回`
        : typeof a.rate === 'string' && /回/.test(a.rate)
          ? a.rate
          : typeof a.rate === 'string' && /^\d+(\.\d+)?%$/.test(a.rate.trim())
            ? a.rate
            : null;
    return {
      id: a.id ?? idx + 1,
      name: a.name || '—',
      cadence: a.cadence || '毎日',
      doneCount,
      rate,
      tone: a.tone || 'good',
    };
  });
}

/** Same rule as kurocoData.resolveFocusTarget: each slot's target belongs to that slot's key. */
function resolveFocusTarget(key, goals) {
  if (!key || !goals) return null;
  const selection = resolveFocusSelection(goals);
  const slot = FOCUS_SLOTS.find((s) => selection[s.keyField] === key);
  if (slot && goals[slot.targetField] != null) return Number(goals[slot.targetField]);
  const sub = resolveSubKgi(goals);
  if (sub && sub.key === key) return sub.target;
  const fromArr = goals.focus?.find((g) => g?.key === key)?.target;
  return fromArr != null && fromArr !== '' ? Number(fromArr) : null;
}

const pctOf = (actual, target) =>
  actual != null && target != null && target > 0 ? Math.round((actual / target) * 100) : null;

export function buildKurocoAiData({
  kpis,
  selectedMonth,
  stylistName,
  selectedStore,
  aiSessions = [],
  goals = null,
  recordedDays = null,
  latestCheckin = null,
}) {
  const p = parseMonth(selectedMonth);
  const now = new Date();
  const daysInMonth = new Date(p.year, p.month, 0).getDate();
  const isCurrentMonth = now.getMonth() + 1 === p.month && now.getFullYear() === p.year;
  const dayOfMonth = isCurrentMonth ? now.getDate() : daysInMonth;
  const daysLeft = Math.max(0, daysInMonth - dayOfMonth);
  // What the stylist actually has left is the runway to the goal date, not the tail of the
  // month being viewed — looking at a closed month said "0 days" while the plan ran for months.
  const goalDate = goals?.goalDate || null;
  const daysToGoal = goalDate
    ? Math.max(0, Math.round((new Date(goalDate) - now) / 86400000))
    : null;

  const salesActual = kpis?.total_sales ?? null;
  const salesTarget = goals?.salesTarget ?? goals?.finalSales ?? null;
  const salesPct =
    salesActual != null && salesTarget != null && salesTarget > 0
      ? Math.round((salesActual / salesTarget) * 100)
      : null;

  const nominationRate = kpis?.nomination_rate ?? null;
  const nomination = nominationRate != null ? `${nominationRate}%` : '—';
  const customers =
    kpis?.customer_count ??
    (kpis?.new_customer_count != null && kpis?.repeat_customer_count != null
      ? kpis.new_customer_count + kpis.repeat_customer_count
      : null);

  const { focusKey, subFocusKey, focusDef, subFocusDef } = resolveFocusSelection(goals);
  const focusActual = focusKey ? readFocusActual(focusKey, kpis, null, selectedMonth) : null;
  const focusTarget = focusKey ? resolveFocusTarget(focusKey, goals) : null;
  const unitActual = subFocusKey ? readFocusActual(subFocusKey, kpis, null, selectedMonth) : null;
  const unitTarget = subFocusKey ? resolveFocusTarget(subFocusKey, goals) : null;
  const unitPct = pctOf(unitActual, unitTarget);

  // One row per 注力 the user set (1–3) — the card no longer draws a fixed ①② pair
  const selection = resolveFocusSelection(goals);
  const focusRows = FOCUS_SLOTS.map((s) => {
    const key = selection[s.keyField];
    if (!key) return null;
    const def = getFocusDef(key);
    const actual = readFocusActual(key, kpis, null, selectedMonth);
    const target = resolveFocusTarget(key, goals);
    return { slot: s.slot, key, icon: def?.icon, label: def?.label || key, unit: def?.unit || null, actual, target, pct: pctOf(actual, target) };
  }).filter(Boolean);

  const sub = resolveSubKgi(goals);
  const subKgiActual = sub ? readFocusActual(sub.key, kpis, null, selectedMonth) : null;
  const subKgiRow = sub
    ? {
        key: sub.key,
        icon: sub.def?.icon,
        label: sub.def?.label || sub.key,
        unit: sub.def?.unit || null,
        actual: subKgiActual,
        target: sub.target,
        pct: pctOf(subKgiActual, sub.target),
      }
    : null;

  const nextMonth = p.month === 12 ? '1月' : `${p.month + 1}月`;
  const name = stylistName ? stylistName.replace(/\s*様$/, '') : 'あなた';
  const goalActions = mapGoalsActions(goals);

  return {
    stylistName: name,
    store: selectedStore || '',
    period: {
      today: now.toISOString().slice(0, 10),
      month: p.label,
      daysLeft,
      isCurrentMonth,
      daysToGoal,
      goalDate,
      dayOfMonth,
      daysInMonth,
    },
    focus: {
      icon: (focusDef || UNSELECTED_FOCUS).icon,
      label: (focusDef || UNSELECTED_FOCUS).label,
      unit: focusDef?.unit || null,
    },
    subFocus: {
      icon: (subFocusDef || UNSELECTED_FOCUS).icon,
      label: (subFocusDef || UNSELECTED_FOCUS).label,
      unit: subFocusDef?.unit || null,
    },
    monthSnap: {
      label: p.label,
      salesActual,
      salesTarget,
      salesPct: salesPct ?? (salesActual != null ? '—' : null),
      nomination,
      customers: customers ?? '—',
      focusActual,
      focusTarget,
      unitActual,
      unitTarget,
      unitPct,
      focusRows,
      subKgi: subKgiRow,
      // Daily pace toward the stylist's own 売上目標 — never invent ¥850,000.
      yenDaily:
        salesActual != null && salesTarget != null && daysLeft > 0
          ? Math.ceil((salesTarget - salesActual) / daysLeft / 1000)
          : null,
      nextMonth,
      nextMonthTarget: focusTarget,
    },
    actions: goalActions,
    recordedDays,
    latestCheckin,
    dailyReview: {
      shortDate: formatShortDate(now),
      // Real answers come from action-checkins; stay empty rather than mock.
      selectedAnswers: [],
      reasons: CHECKIN_REASONS,
    },
    aiSessions,
    dateDividerLabel: formatTodayJa(now),
  };
}

const EMPTY_CHECKINS = { recordedDays: null, latestCheckin: null };

/**
 * 行動記録 for the selected month.
 * `recordedDays` = true「記録N日分」; null on unknown/fail so the label is omitted
 * (never guess from calendar day-of-month).
 */
function useActionCheckins(store, stylist, selectedMonth) {
  const [state, setState] = useState(EMPTY_CHECKINS);

  useEffect(() => {
    if (!store || !stylist || !/^\d{4}-\d{2}$/.test(selectedMonth || '')) {
      setState(EMPTY_CHECKINS);
      return undefined;
    }
    let cancelled = false;
    const [y, m] = selectedMonth.split('-').map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    fetchActionCheckins({
      store,
      stylist,
      start: `${selectedMonth}-01`,
      end: `${selectedMonth}-${String(lastDay).padStart(2, '0')}`,
    })
      .then((res) => {
        if (cancelled) return;
        const list = res.checkins || [];
        // API returns date DESCENDING, so [0] is the most recent day.
        setState({ recordedDays: list.length, latestCheckin: list[0] || null });
      })
      .catch(() => {
        if (!cancelled) setState(EMPTY_CHECKINS);
      });
    return () => {
      cancelled = true;
    };
  }, [store, stylist, selectedMonth]);

  return state;
}

export function useKurocoAiData(stylistName, aiSessions = []) {
  const { user } = useAuth();
  const { kpis, selectedMonth, selectedStore, selectedStylist } = useDashboard();
  const [goals] = useGoalsConfig(user?.uid, selectedStore, selectedStylist);
  const { recordedDays, latestCheckin } = useActionCheckins(
    selectedStore,
    selectedStylist,
    selectedMonth
  );
  return useMemo(
    () =>
      buildKurocoAiData({
        kpis,
        selectedMonth,
        stylistName,
        selectedStore,
        aiSessions,
        goals,
        recordedDays,
        latestCheckin,
      }),
    [
      kpis,
      selectedMonth,
      stylistName,
      selectedStore,
      aiSessions,
      goals,
      recordedDays,
      latestCheckin,
    ]
  );
}

export default buildKurocoAiData;
